// src/ConsumerApp/context/AuthModalContext.js

import React, { createContext, useContext, useState, useEffect, useMemo, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';

const AuthModalContext = createContext();
const GUEST_CART_SESSION_KEY = 'bb_guest_cart_v1';
const BASE_URL = process.env.REACT_APP_FORE_BRICKBUCKET || '';

export function AuthModalProvider({ children }) {
  const queryClient = useQueryClient();
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);

  // Read lightweight guest cart from sessionStorage only
  const [guestCart, setGuestCart] = useState(() => {
    try {
      const serialized = sessionStorage.getItem(GUEST_CART_SESSION_KEY);
      return serialized ? JSON.parse(serialized) : [];
    } catch {
      return [];
    }
  });

  // Keep sessionStorage in sync
  useEffect(() => {
    try {
      if (guestCart.length === 0) {
        sessionStorage.removeItem(GUEST_CART_SESSION_KEY);
      } else {
        sessionStorage.setItem(GUEST_CART_SESSION_KEY, JSON.stringify(guestCart));
      }
    } catch (e) {
      console.warn('Storage sync failed:', e);
    }
  }, [guestCart]);

  // Strip down raw product payload to its leanest representation
  const addToGuestCart = useCallback((product, quantity = 1) => {
    const rawId = product?._id || product?.product_id || product?.id;
    if (!rawId) return;

    const unitPrice = Number(
      product?.buy_now?.unit_price ??
      product?.selling_price ??
      product?.unit_price ??
      product?.price ??
      0
    );

    setGuestCart((prev) => {
      const existingIndex = prev.findIndex((item) => item.id === rawId);
      if (existingIndex > -1) {
        const next = [...prev];
        next[existingIndex] = {
          ...next[existingIndex],
          qty: next[existingIndex].qty + quantity,
        };
        return next;
      }

      return [
        ...prev,
        {
          id: rawId,
          qty: quantity,
          price: unitPrice,
          name: product.product_name || product.name || 'Building Material',
          img: product.product_image || product.product_image_url || product.image || '',
        },
      ];
    });
  }, []);

  const updateGuestItemQty = useCallback((id, newQty) => {
    setGuestCart((prev) => {
      if (newQty <= 0) {
        return prev.filter((item) => item.id !== id);
      }
      return prev.map((item) => (item.id === id ? { ...item, qty: newQty } : item));
    });
  }, []);

  const clearGuestCart = useCallback(() => {
    setGuestCart([]);
    sessionStorage.removeItem(GUEST_CART_SESSION_KEY);
  }, []);

  // Sync to server bucket upon successful login
  const syncGuestCartToServer = useCallback(
    async (customerId) => {
      if (!guestCart.length || !customerId) return;

      try {
        const endpoint = `${BASE_URL}brickbucket/bucket/add-item-to-bucket`;
        await Promise.all(
          guestCart.map((item) =>
            fetch(endpoint, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                customer_id: customerId,
                item_id: item.id,
                purchase_type: 'BUY_NOW',
                quantity: item.qty,
              }),
            })
          )
        );

        clearGuestCart();
        queryClient.invalidateQueries({ queryKey: ['bucketSummary', customerId] });
        queryClient.invalidateQueries({ queryKey: ['bucketItems', customerId] });
      } catch (err) {
        console.warn('Cart sync failed:', err);
      }
    },
    [guestCart, clearGuestCart, queryClient]
  );

  // Normalized items view formatted for Bucket UI compatibility
  const normalizedGuestItems = useMemo(() => {
    return guestCart.map((item) => ({
      item_id: item.id,
      product_id: item.id,
      _id: item.id,
      item_name: item.name,
      product_name: item.name,
      item_image: item.img,
      product_image: item.img,
      price: item.price,
      quantity: item.qty,
    }));
  }, [guestCart]);

  const guestCartCount = useMemo(() => {
    return guestCart.reduce((sum, item) => sum + item.qty, 0);
  }, [guestCart]);

  const guestSubtotal = useMemo(() => {
    return guestCart.reduce((sum, item) => sum + item.price * item.qty, 0);
  }, [guestCart]);

  const value = useMemo(
    () => ({
      isLoginModalOpen,
      openLoginModal: () => setIsLoginModalOpen(true),
      closeLoginModal: () => setIsLoginModalOpen(false),
      guestCart: normalizedGuestItems,
      guestCartCount,
      guestSubtotal,
      addToGuestCart,
      updateGuestItemQty,
      clearGuestCart,
      syncGuestCartToServer,
    }),
    [
      isLoginModalOpen,
      normalizedGuestItems,
      guestCartCount,
      guestSubtotal,
      addToGuestCart,
      updateGuestItemQty,
      clearGuestCart,
      syncGuestCartToServer,
    ]
  );

  return <AuthModalContext.Provider value={value}>{children}</AuthModalContext.Provider>;
}

export const useAuthModal = () => useContext(AuthModalContext);